import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "components/fields/InputField";
import { useAuth } from "context/AuthProvider";
import authImg from "assets/img/auth/logo.svg";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import Request from "helpers/request";
import CommonNotification from "components/common/CommonNotification";

function Login() {
  const { login, retrieveToken } = useAuth();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const token = retrieveToken();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
        "Invalid email address"
      )
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
  });

  useEffect(() => {
    if (token) {
      navigate("/admin");
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Your login action here
      setValidationErrors({});
      // values.preventDefault();
      setIsSubmitting(true);
      Request.postJson("login/", values)
        .then((r) => {
          setIsSubmitting(false);
          localStorage.setItem("token", r.data.data.access_token);
          CommonNotification.success("Login successful");
          navigate("/admin");
          const token = r.data.data.access_token;
          login(token);
        })
        .catch((e) => {
          setIsSubmitting(false);
          if (e.response.data.detail !== undefined) {
            setValidationErrors(e.response.data.detail);
          }
          if (e.response.data.error !== undefined) {
            setValidationErrors(e.response.data.error);
          }
          CommonNotification.error("Login failed");
          console.log(e);
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="mt-16 mb-16 flex w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-center">
        {/* Sign in section */}
        <div className="mt-[10vh] w-full max-w-full flex-col items-center rounded-xl p-6 shadow-[0_0px_6px_rgba(0,0,0,0.1)] xl:max-w-[420px]">
          <img
            className="mt-[-70px] mr-auto ml-auto rounded-[50px] bg-white"
            src={authImg}
            alt=""
          />
          <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">
            Sign In
          </h4>
          <p className="mb-4 ml-1 text-base text-gray-600">
            Enter your email and password to sign in!
          </p>
          {Object.keys(validationErrors).length !== 0 && (
            <small className="mb-3 ml-1 text-center text-red-600">
              {validationErrors.message}
            </small>
          )}
          {/* Email */}
          <div className="mb-3">
            <InputField
              required="*"
              variant="auth"
              extra=""
              label="Email"
              placeholder="mail@johndoe.com"
              id="email"
              type="text"
              name="email"
              value={formik.values.email}
              onchange={formik.handleChange}
              state={formik.touched.email && formik.errors.email ? "error" : ""}
            />
            {formik.touched.email && formik.errors.email && (
              <small className="ml-1.5 text-[12px] text-red-600">
                {formik.errors.email}
              </small>
            )}
          </div>
          {/* Password */}
          <div className="relative mb-3">
            <InputField
              required="*"
              variant="auth"
              extra=""
              label="Password"
              placeholder="Min 8 characters"
              id="password"
              type={showPassword ? "text" : "password"} // Toggle between "text" and "password"
              name="password"
              value={formik.values.password}
              onchange={formik.handleChange}
              state={
                formik.touched.password && formik.errors.password ? "error" : ""
              }
            />
            <span
              className={`absolute top-11 right-2 cursor-pointer text-gray-600`}
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <AiFillEye /> : <AiFillEyeInvisible />}
            </span>
            {formik.touched.password && formik.errors.password && (
              <small className="ml-1.5 text-[12px] text-red-600">
                {formik.errors.password}
              </small>
            )}
          </div>
          {/* Checkbox */}
          <div className="mt-3 mb-4 flex items-center justify-between px-2">
            {/* <div className="flex items-center">
              <Checkbox />
              <p className="ml-2 text-sm font-medium text-navy-700 dark:text-white">
                Keep me logged In
              </p>
            </div> */}
            {
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
              >
                Forgot Password?
              </Link>
            }
          </div>
          <button
            disabled={isSubmitting}
            type="submit"
            className="linear mt-2 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {isSubmitting ? "Signing in..." : "Sign In"}
          </button>
          <div className="mt-4">
            <span className=" text-sm font-medium text-navy-700 dark:text-gray-600">
              Not registered yet?
            </span>
            {
              <Link
                to="/register"
                className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-white"
              >
                Create an account
              </Link>
            }
          </div>
        </div>
      </div>
    </form>
  );
}
export default Login;
